import { DEFAULT_RADIUS, SKIN_PARAM_RANGES } from '../constants';
import type { EffectStyleProps } from './hooks/useComputedStyle';
import type {
  RevealEffectConfiguration,
  SwarmEffectConfiguration,
  PerlinEffectConfiguration,
  EffectConfiguration,
  StartPosition,
  ParticleColorConfiguration,
  ParticleOpacityConfiguration,
  ParticleSizeConfiguration,
} from './TextParticles.types';
import { AnimationType } from './TextParticles.types';
import { getDevicePixelRatio } from './utils/canvas-utils';
import { normalize } from './utils/math-utils';
import { lerp } from '@wix/wow-utils';

export const getEffectConfiguration = (
  effectProps: EffectStyleProps | undefined,
  isEditor: boolean,
): EffectConfiguration | null => {
  if (!effectProps) {
    return null;
  }

  const devicePixelRatio = getDevicePixelRatio();
  // For devicePixelRatio=3 (mobile screens) cap the DPR at 1 because it makes the particles look too large relative to text
  const adjustedDPR = devicePixelRatio <= 2 ? devicePixelRatio : 1;

  const effectType = effectProps.effectType;
  const animationType = effectProps.animationType;
  const animationDuration = effectProps.animationDuration * 1000;

  switch (effectType) {
    case 'perlin': {
      const perlinEffectConfiguration: PerlinEffectConfiguration = {
        speed: effectProps.speed,
        noise: effectProps.noise,
        amplitude: effectProps.amplitude,
        effectType,
        animationDuration: isEditor ? 10000 : Infinity,
        presetId: effectProps.presetId,
      };
      return perlinEffectConfiguration;
    }
    case 'swarm': {
      const normalizedParams = getNormalizedSwarmParams({
        speed: effectProps.speed,
        turbulence: effectProps.turbulence,
      });

      const swarmEffectConfiguration: SwarmEffectConfiguration = {
        startPosition: effectProps.startPosition,
        radius: DEFAULT_RADIUS * adjustedDPR,
        effectType,
        animationDuration,
        animationType,
        speed: normalizedParams.speed * devicePixelRatio,
        turbulence: normalizedParams.turbulence * devicePixelRatio,
        presetId: effectProps.presetId,
      };

      return swarmEffectConfiguration;
    }
    case 'reveal': {
      const particleDuration = effectProps.particleDuration * 1000;

      const animationDirection =
        animationType === AnimationType.IN
          ? effectProps.animationDirection
          : (effectProps.animationDirection + 180) % 360;

      const normalizedParams = getNormalizedRevealParams({
        speed: effectProps.speed,
        turbulence: effectProps.turbulence,
        particleStartSize: effectProps.particleStartSize,
        particleEndSize: effectProps.particleEndSize,
      });

      const revealEffectConfiguration: RevealEffectConfiguration = {
        windDirection: effectProps.windDirection,
        animationDirection,
        particleDuration,
        // The adjustedDPR is only used for the visual size of particles (radius), not for coordinate space scaling.
        windSpeed: normalizedParams.windSpeed * devicePixelRatio,
        turbulence: normalizedParams.turbulence * devicePixelRatio,
        particleColorCount: effectProps.particleColorCount,
        particleColor1: `rgb(${effectProps.particleColor1})`,
        particleColor2: `rgb(${effectProps.particleColor2})`,
        particleColor3: `rgb(${effectProps.particleColor3})`,
        particleColor4: `rgb(${effectProps.particleColor4})`,
        radius: DEFAULT_RADIUS * adjustedDPR,
        effectType,
        animationType,
        animationDuration,
        particleStartSize: normalizedParams.particleStartSize,
        particleEndSize: normalizedParams.particleEndSize,
        particleStartOpacity: effectProps.particleStartOpacity,
        particleEndOpacity: effectProps.particleEndOpacity,
        presetId: effectProps.presetId,
      };
      return revealEffectConfiguration;
    }

    default:
      const _exhaustiveCheck: never = effectType;
      throw new Error(`Unsupported effect type ${_exhaustiveCheck}`);
  }
};

export const hasAnimationType = (
  config: EffectConfiguration | null,
): config is EffectConfiguration & { animationType: AnimationType } => {
  return config !== null && 'animationType' in config;
};

export const isPerlinEffect = (
  effectConfiguration: EffectConfiguration,
): effectConfiguration is PerlinEffectConfiguration =>
  effectConfiguration.effectType === 'perlin';

export const isSwarmEffect = (
  effectConfiguration: EffectConfiguration,
): effectConfiguration is SwarmEffectConfiguration =>
  effectConfiguration.effectType === 'swarm';

export const isRevealEffect = (
  effectConfiguration: EffectConfiguration,
): effectConfiguration is RevealEffectConfiguration =>
  effectConfiguration.effectType === 'reveal';

export const hasStartPosition = (
  effectConfiguration: EffectConfiguration | null,
): effectConfiguration is EffectConfiguration & {
  startPosition: StartPosition;
} => effectConfiguration !== null && 'startPosition' in effectConfiguration;

export const hasParticleSize = (
  effectConfiguration: EffectConfiguration | null,
): effectConfiguration is EffectConfiguration & ParticleSizeConfiguration =>
  effectConfiguration !== null &&
  'particleStartSize' in effectConfiguration &&
  'particleEndSize' in effectConfiguration;

export const hasParticleOpacity = (
  effectConfiguration: EffectConfiguration | null,
): effectConfiguration is EffectConfiguration & ParticleOpacityConfiguration =>
  effectConfiguration !== null &&
  'particleStartOpacity' in effectConfiguration &&
  'particleEndOpacity' in effectConfiguration;

export const getColorProgression = (
  particleColorConfig: ParticleColorConfiguration,
) =>
  [
    particleColorConfig.particleColor1,
    particleColorConfig.particleColor2,
    particleColorConfig.particleColor3,
    particleColorConfig.particleColor4,
  ].slice(0, particleColorConfig.particleColorCount);

const getNormalizedSwarmParams = ({
  speed,
  turbulence,
}: {
  speed: number;
  turbulence: number;
}) => {
  const windStrengthRange = { min: 0.3, max: 1.2 };
  const turbulenceScaleRange = { min: 5, max: 50 };

  const normalizedSpeed =
    Math.round(
      lerp(
        windStrengthRange.min,
        windStrengthRange.max,
        normalize(
          speed,
          SKIN_PARAM_RANGES.speed.min,
          SKIN_PARAM_RANGES.speed.max,
        ),
      ) * 10,
    ) / 10;

  const normalizedTurbulence =
    Math.round(
      lerp(
        turbulenceScaleRange.min,
        turbulenceScaleRange.max,
        normalize(
          turbulence,
          SKIN_PARAM_RANGES.turbulence.min,
          SKIN_PARAM_RANGES.turbulence.max,
        ),
      ) * 10,
    ) / 10;

  return { speed: normalizedSpeed, turbulence: normalizedTurbulence };
};

const getNormalizedRevealParams = ({
  speed,
  turbulence,
  particleStartSize,
  particleEndSize,
}: {
  speed: number;
  turbulence: number;
  particleStartSize: number;
  particleEndSize: number;
}) => {
  const windSpeedRange = { min: 0, max: 4 };
  const turbulenceRange = { min: 0, max: 3 };
  const particleSizeRange = { min: 0.1, max: 4 };

  const normalizedWindSpeed =
    Math.round(
      lerp(
        windSpeedRange.min,
        windSpeedRange.max,
        normalize(
          speed,
          SKIN_PARAM_RANGES.speed.min,
          SKIN_PARAM_RANGES.speed.max,
        ),
      ) * 10,
    ) / 10;

  const normalizedTurbulence =
    Math.round(
      lerp(
        turbulenceRange.min,
        turbulenceRange.max,
        normalize(
          turbulence,
          SKIN_PARAM_RANGES.turbulence.min,
          SKIN_PARAM_RANGES.turbulence.max,
        ),
      ) * 10,
    ) / 10;

  const normalizedParticleStartSize =
    Math.round(
      lerp(
        particleSizeRange.min,
        particleSizeRange.max,
        normalize(
          particleStartSize,
          SKIN_PARAM_RANGES['particle-start-size'].min,
          SKIN_PARAM_RANGES['particle-start-size'].max,
        ),
      ) * 10,
    ) / 10;

  const normalizedParticleEndSize =
    Math.round(
      lerp(
        particleSizeRange.min,
        particleSizeRange.max,
        normalize(
          particleEndSize,
          SKIN_PARAM_RANGES['particle-end-size'].min,
          SKIN_PARAM_RANGES['particle-end-size'].max,
        ),
      ) * 10,
    ) / 10;

  return {
    windSpeed: normalizedWindSpeed,
    turbulence: normalizedTurbulence,
    particleStartSize: normalizedParticleStartSize,
    particleEndSize: normalizedParticleEndSize,
  };
};

export const getNormalizedPerlinParams = (
  effectConfiguration: PerlinEffectConfiguration,
): { noiseScale: number; driftSpeed: number; displacementStrength: number } => {
  const { speed, noise, amplitude } = effectConfiguration;
  const driftSpeedRange = { min: 0.0002, max: 0.001 };
  const noiseRange = { min: 150, max: 1500 }; // distortion, smaller is more distortion
  const amplitudeRange = { min: 10, max: 100 };

  const driftSpeed = lerp(
    driftSpeedRange.min,
    driftSpeedRange.max,
    normalize(speed, SKIN_PARAM_RANGES.speed.min, SKIN_PARAM_RANGES.speed.max),
  );

  const noiseScale = lerp(
    noiseRange.max,
    noiseRange.min,
    normalize(noise, SKIN_PARAM_RANGES.noise.min, SKIN_PARAM_RANGES.noise.max),
  );

  const displacementStrength = lerp(
    amplitudeRange.min,
    amplitudeRange.max,
    normalize(
      amplitude,
      SKIN_PARAM_RANGES.amplitude.min,
      SKIN_PARAM_RANGES.amplitude.max,
    ),
  );
  return { noiseScale, driftSpeed, displacementStrength };
};

export const getParticleRadius = (effectConfiguration: EffectConfiguration) => {
  if ('radius' in effectConfiguration) {
    return effectConfiguration.radius;
  }

  return DEFAULT_RADIUS;
};

export const getAnimationType = (
  effectConfiguration: EffectConfiguration,
): AnimationType => {
  if ('animationType' in effectConfiguration) {
    return effectConfiguration.animationType;
  }
  return AnimationType.IN;
};

export const getTextContentPadding = (
  effectConfiguration: EffectConfiguration,
) => {
  if (isPerlinEffect(effectConfiguration)) {
    return Math.ceil(
      getNormalizedPerlinParams(effectConfiguration).displacementStrength,
    );
  }
  return 0;
};
